import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { process } from "@progress/kendo-data-query";
import {
  Container,
  Row,
  Col,
  Label,
  Form,
  Input,
  UncontrolledTooltip,
} from "reactstrap";

import PropTypes from "prop-types";
import FeatherIcon from "feather-icons-react";

import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { ColumnMenu } from "../components/kendoComponents/columnMenu/ColumnMenu";
import { ColumnProps } from "../components/kendoComponents/columnProps/ColumnProps";
import { GridPager } from "../components/kendoComponents/gridPager/GridPager";
import { HtmlCell } from "../components/kendoComponents/htmlCell/HtmlCell";

import {
  DropDownButton,
  DropDownButtonItem,
} from "@progress/kendo-react-buttons";


import "../assets/scss/kendo-theme-custom.scss";
import "react-hammerjs";

import LoaderSpin from "../components/loaderSpin/LoaderSpin";

import { useNavigate, useSearchParams } from "react-router-dom";

import FilterSubmit from "../components/DataQuality/FilterSubmit";

import FilterOverlay from "../components/filterOverlay/FilterOverlay";
import DataProductTreeSelector from "../components/DataQuality/DataProductTreeSelector";
import OwnersSelector from "../components/DataQuality/OwnersSelector";
import {
  fetchDataCatalog,
  setFilterDataProducts,
  setFilterOwner,
  toogleDataCheck,
  setFullTextSearch,
  setIsCompleteSearch,
} from "../store/app/DataQualityStatus/DataQualitySlice";


const RowTooltipCell = ({ dataItem, dataIndex }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const tooltipValue = dataItem?.row_tooltip;
  const uniqueId = `tooltip-${dataItem?.type_id ?? dataIndex}`;

  if (!tooltipValue || String(tooltipValue).trim() === "") {
    return <td className="text-center" />;
  }

  const formatTooltipContent = (text) => {
    const normalized = String(text).replaceAll(/\r\n|\r|\n/g, "\n");
    return normalized.split("\n").map((line) => {
      const key = `line_${line.trim().slice(0, 20)}_${line.length}`;
      return (
        <div key={key} dangerouslySetInnerHTML={{ __html: line.trim() }} />
      );
    });
  };

  const handleClick = () => {
    const params = new URLSearchParams(searchParams);
    params.set("sortHtml", "true");
    navigate(`/data-catalog/details/${dataItem.type_id}?${params.toString()}`);
  };

  return (
    <td className="text-center">
      <button
        type="button"
        id={uniqueId}
        className="text-primary"
        style={{ cursor: "pointer", border: "none", background: "none", padding: 0 }}
        onClick={handleClick}
        aria-label={`Open data product details for ${dataItem?.type_name || "item"}`}
      >
        <FeatherIcon icon="info" size={16} style={{ pointerEvents: "none" }} />
      </button>
      <UncontrolledTooltip
        target={uniqueId}
        placement="top"
        className="text-start"
        style={{ maxWidth: "300px" }}
      >
        <div className="p-2">{formatTooltipContent(tooltipValue)}</div>
      </UncontrolledTooltip>
    </td>
  );
};

RowTooltipCell.propTypes = {
  dataItem: PropTypes.object.isRequired,
  dataIndex: PropTypes.number.isRequired,
};

const DataProductNameCell = ({ dataItem }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return (
    <HtmlCell
      value={dataItem.type_name}
      onClick={() => {
        searchParams.delete("sortHtml");
        navigate("/data-catalog/details/" + dataItem.type_id + "?" + searchParams);
      }}
      className="fw-bold text-decoration-underline"
    />
  );
};

DataProductNameCell.propTypes = {
  dataItem: PropTypes.object.isRequired,
};

const TypeDescriptionCell = ({ dataItem }) => (
  <HtmlCell value={dataItem.type_description} />
);

TypeDescriptionCell.propTypes = {
  dataItem: PropTypes.object.isRequired,
};

const ActionsCell = ({ dataItem }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dataProducts = useSelector(
    (state) => state.dataQualityReducer.dataProducts
  );

  return (
    <td>
      <DropDownButton
        buttonClass="border-0"
        iconClass="k-icon k-i-menu"
        popupClass="p-3"
        rounded={null}
        ariaLabel="Azioni"
        ariaRole="button"
        onItemClick={(event) => {
          if (event.item.text === "/versioni") {
            const matchingProduct = dataProducts.find(
              (p) => p.type_id == dataItem.type_id
            );
            const productId = matchingProduct ? matchingProduct.productId : null;
            const p = new URLSearchParams();
            p.set("interval", "-3");
            if (productId != null) {
              p.set("dataproducts", JSON.stringify([productId]));
            }
            navigate(`/versioni?${p.toString()}`);
          } else {
            searchParams.delete("sortHtml");
            navigate(event.item.text + "?" + searchParams.toString());
          }
        }}
      >
        <DropDownButtonItem
          text={"/versioni/data-lineage/" + dataItem.type_id}
          render={() => <li>Product Lineage</li>}
        />
        <DropDownButtonItem
          text="/versioni"
          render={() => <li>Quick Search</li>}
        />
        <DropDownButtonItem
          text={"/data-catalog/details/" + dataItem.type_id}
          render={() => <li>Data Product Details</li>}
        />
      </DropDownButton>
    </td>
  );
};

ActionsCell.propTypes = {
  dataItem: PropTypes.object.isRequired,
};

const DataCatalog = () => {
  const dispatch = useDispatch();


  let [searchParams] = useSearchParams();

  const isSpinnerVisible = useSelector(
    (state) => state.dataQualityReducer.loading
  );
  const dataCheck = useSelector((state) => state.dataQualityReducer.dataCheck);
  const refreshCount = useSelector(
    (state) => state.dataQualityReducer.refreshCount
  );

  const filterDataProducts = useSelector(
    (state) => state.dataQualityReducer.filterDataProducts
  );

  const filterOwner = useSelector(
    (state) => state.dataQualityReducer.filterOwner
  );

  const fullTextSearch = useSelector(
    (state) => state.dataQualityReducer.fullTextSearch
  );

  const isCompleteSearch = useSelector(
    (state) => state.dataQualityReducer.isCompleteSearch
  );

  const dataCatalog = useSelector(
    (state) => state.dataQualityReducer.dataCatalog
  );

  const [dataState, setDataState] = useState({
    sort: [
      {
        field: "code",
        dir: "asc",
      },
    ],

  });

  const [dataResult, setDataResult] = useState(process(dataCatalog, dataState));

  useEffect(() => {
    dispatch(
      fetchDataCatalog(
        filterDataProducts,
        filterOwner,
        fullTextSearch,
        isCompleteSearch
      )
    );
  }, [refreshCount]);

  useEffect(() => {
    setDataResult(process(dataCatalog, dataState));
  }, [dataCatalog]);

   const handleFullTextChange = (e) => {
    dispatch(toogleDataCheck(true));

    searchParams.delete("dataproducts");
    dispatch(setFilterDataProducts([]));

    searchParams.delete("owner");
    dispatch(setFilterOwner(""));

    searchParams.set("fulltext", e.target.value);
    dispatch(setFullTextSearch(e.target.value));
  };

  const handleFullTextKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      dispatch(toogleDataCheck(false));
      dispatch(
        fetchDataCatalog(
          filterDataProducts,
          filterOwner,
          e.currentTarget.value,
          isCompleteSearch
        )
      );
    }
  };




  return (
    <>
<Form>
        <Row className="bg-light-primary px-4 py-2">
          <Col>
            <Row className="align-items-end g-3">
              <Col xs="12" sm="6" md="3" lg="2" className="flex-grow-1">
                <Label for="multiSelectTreeDataProducts" className="mb-1 small">Data Product</Label>
                <DataProductTreeSelector />
              </Col>
              
              <Col xs="12" sm="6" md="4" lg="3" className="flex-grow-1">
                <Label for="ownersSelector" className="mb-1 small">User</Label>
                <OwnersSelector />
              </Col>
              
              <Col xs="12" sm="12" md="5" lg="4" className="flex-grow-1">
                <Label for="fullTextSearch" className="mb-1 small">Full Text Search</Label>
                <div className="d-flex gap-2 align-items-center">
                  <Input
                    id="fullTextSearch"
                    name="fullTextSearch"
                    placeholder="Search..."
                    type="text"
                    value={fullTextSearch}
                    onChange={handleFullTextChange}
                    onKeyDown={handleFullTextKeyDown}
                    style={{ flex: 1 }}
                  />
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <Input
                      id="isCompleteSearch"
                      name="isCompleteSearch"
                      type="checkbox"
                      className="form-check-input"
                      checked={isCompleteSearch}
                      onChange={(e) => {
                        searchParams.set("isCompleteSearch", e.target.checked);
                        dispatch(setIsCompleteSearch(e.target.checked));
                        dispatch(toogleDataCheck(true));
                      }}
                    />
                    <Label
                      for="isCompleteSearch"
                      check
                      className="form-check-label mb-0"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Extended search
                    </Label>
                    <span
                      id="searchModeTooltip"
                      className="text-primary"
                      style={{ cursor: "help" }}
                    >
                      <FeatherIcon
                        icon="info"
                        size={16}
                        style={{ pointerEvents: "none" }}
                      />
                    </span>
                  </div>
                </div>
                <UncontrolledTooltip
                  target="searchModeTooltip"
                  placement="right"
                  className="text-start"
                  style={{ maxWidth: "300px" }}
                >
                  <div className="p-2">
                    <p className="mb-2 fw-bold">Search modes:</p>
                    <ul className="ps-3 mb-0 small">
                      <li className="mb-1">
                        <strong>Default:</strong> Lexeme-based full-text search - Only the most relevant results
                      </li>
                      <li>
                        <strong>Extended:</strong> Substring full-text search - All matching results
                      </li>
                    </ul>
                  </div>
                </UncontrolledTooltip>
              </Col>

              <Col xs="12" sm="auto" className="d-flex align-items-end">
                <FilterSubmit color="primary" text="Filter" />
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>


      <Container fluid className="py-2 px-4  position-relative ">
        <Row>
          <Col>
            <div className="grid_task_container_tabs border overflow-x-scroll position-relative">
              <div className="position-absolute w-100 h-100 top-0 start-0">
                <Grid
                  style={{ height: "100%" }}
                  scrollable="scrollable"
                  fixedScroll={true}
                  sortable={true}
                  total={dataResult?.length}
                  data={dataResult}
                  pager={(props) =>
                    GridPager(props, dataCatalog, "data_catalog")
                  }
                  {...dataState}
                  onDataStateChange={(e) => {
                    setDataState(e.dataState);
                    setDataResult(process(dataCatalog, e.dataState));
                  }}
                >
                  <Column
                    width="60"
                    cell={RowTooltipCell}
                  />
                  <Column
                    {...ColumnProps("type_name", dataState)}
                    title="Data Product Name"
                    cell={DataProductNameCell}
                    columnMenu={ColumnMenu}
                  />
                  <Column
                    {...ColumnProps("type_description", dataState)}
                    title="Description"
                    cell={TypeDescriptionCell}
                    columnMenu={ColumnMenu}
                  />
                  <Column
                    {...ColumnProps("domain_name", dataState)}
                    title="Domain"
                    columnMenu={ColumnMenu}
                  />
                  <Column
                    title=" "
                    width="60"
                    cell={ActionsCell}
                  />
                </Grid>
              </div>
            </div>
          </Col>
        </Row>
        <LoaderSpin isVisible={isSpinnerVisible} />
        <FilterOverlay isVisible={dataCheck} />
      </Container>
    </>
  );
};

export default DataCatalog;
